import React, { useEffect, useState } from "react";
import { adminGetCheckoutSettings, adminSaveCheckoutSettings } from "../../api/settingsApi";
import "./AdminConsultations.css"; // shared ac-* admin form styles

const text = (v) => (v == null ? "" : String(v));
const amount = (v) => (v === "" ? 0 : Number(v));
const optionalAmount = (v) => (v === "" ? null : Number(v));

const fromApi = (s) => ({
  shippingFee: text(s.shippingFee),
  freeShippingAbove: text(s.freeShippingAbove),
  codFee: text(s.codFee),
  codEnabled: Boolean(s.codEnabled),
  codRequiresAccount: s.codRequiresAccount !== false,
  codAdvanceEnabled: Boolean(s.codAdvanceEnabled),
  codAdvanceAbove: text(s.codAdvanceAbove),
  codAdvanceAmount: text(s.codAdvanceAmount),
  codAdvanceHighAbove: text(s.codAdvanceHighAbove),
  codAdvanceHighAmount: text(s.codAdvanceHighAmount),
  codAdvanceCoversDelivery: Boolean(s.codAdvanceCoversDelivery),
  prepaidDiscountType: s.prepaidDiscountType || "none",
  prepaidDiscountValue: text(s.prepaidDiscountValue),
  prepaidDiscountMax: text(s.prepaidDiscountMax),
  deliveryMinDays: text(s.deliveryMinDays),
  deliveryMaxDays: text(s.deliveryMaxDays),
  unserviceablePincodes: (s.unserviceablePincodes || []).join(", "),
  addOnMaxPrice: text(s.addOnMaxPrice),
});

// What is sent to the server (the server validates it again).
export const toPayload = (form) => ({
  shippingFee: amount(form.shippingFee),
  codFee: amount(form.codFee),
  freeShippingAbove: optionalAmount(form.freeShippingAbove),
  codEnabled: form.codEnabled,
  codRequiresAccount: form.codRequiresAccount,
  codAdvanceEnabled: form.codAdvanceEnabled,
  codAdvanceAbove: amount(form.codAdvanceAbove),
  codAdvanceAmount: amount(form.codAdvanceAmount),
  codAdvanceHighAbove: optionalAmount(form.codAdvanceHighAbove),
  codAdvanceHighAmount: amount(form.codAdvanceHighAmount),
  codAdvanceCoversDelivery: form.codAdvanceCoversDelivery,
  prepaidDiscountType: form.prepaidDiscountType,
  prepaidDiscountValue: amount(form.prepaidDiscountValue),
  prepaidDiscountMax: optionalAmount(form.prepaidDiscountMax),
  deliveryMinDays: amount(form.deliveryMinDays),
  deliveryMaxDays: amount(form.deliveryMaxDays),
  unserviceablePincodes: form.unserviceablePincodes,
  addOnMaxPrice: amount(form.addOnMaxPrice),
});

// Admin -> Checkout Charges. These are the only shipping / COD charges and discounts the
// store applies; customers see them on the checkout from the first screen.
export default function AdminCheckoutSettings({ token }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const flash = (type, message) => {
    setNotice({ type, text: message });
    setTimeout(() => setNotice(null), 4000);
  };

  useEffect(() => {
    adminGetCheckoutSettings(token).then((result) => {
      if (result.ok && result.success) setForm(fromApi(result.data));
      else flash("error", result.message || "Unable to load the checkout settings");
    });
  }, [token]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await adminSaveCheckoutSettings(toPayload(form), token);
    setSaving(false);
    if (result.ok && result.success) {
      setForm(fromApi(result.data));
      flash("success", "Checkout settings saved");
    } else {
      flash("error", result.message || "Unable to save");
    }
  };

  if (!form) return notice ? <div className={`feedback-alert ${notice.type}`}>{notice.text}</div> : <p>Loading...</p>;

  const num = (name, label, extra = {}) => (
    <label>
      {label}
      <input
        className="ac-input"
        type="number"
        min="0"
        step={extra.step || "1"}
        placeholder={extra.placeholder}
        disabled={extra.disabled}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
      />
    </label>
  );
  const check = (name, label) => (
    <label className="ac-check">
      <input type="checkbox" checked={form[name]} onChange={(e) => set(name, e.target.checked)} /> {label}
    </label>
  );
  const prepaidOn = form.prepaidDiscountType !== "none";

  return (
    <div className="panel-tab ac-wrap">
      {notice && <div className={`feedback-alert ${notice.type}`}>{notice.text}</div>}

      <form className="ac-form" onSubmit={save}>
        <h3>Checkout charges</h3>
        <p className="user-email">
          Shown to the customer on the very first checkout screen, so nothing new is added at the last step. All amounts in ₹.
        </p>

        <div className="ac-grid">
          {num("shippingFee", "Shipping fee")}
          {num("freeShippingAbove", "Free shipping above (optional)", { placeholder: "e.g. 999" })}
          {num("codFee", "COD fee")}
        </div>

        <h3>Pay online and save (prepaid discount)</h3>
        <p className="user-email">
          A cash discount for paying online, shown at checkout to move customers away from Cash on Delivery. It is taken off the
          goods after any coupon, and is never given on a COD order.
        </p>
        <div className="ac-grid">
          <label>
            Discount type
            <select className="ac-input" value={form.prepaidDiscountType} onChange={(e) => set("prepaidDiscountType", e.target.value)}>
              <option value="none">No prepaid discount</option>
              <option value="flat">Flat amount (₹)</option>
              <option value="percent">Percentage of the order (%)</option>
            </select>
          </label>
          {num("prepaidDiscountValue", form.prepaidDiscountType === "percent" ? "Discount (%)" : "Discount (₹)", { step: "0.01", disabled: !prepaidOn })}
          {form.prepaidDiscountType === "percent" && num("prepaidDiscountMax", "Largest discount in ₹ (optional)", { placeholder: "e.g. 150" })}
        </div>

        <h3>Cash on Delivery</h3>
        {check("codEnabled", "Allow Cash on Delivery")}
        {check("codRequiresAccount", "Cash on Delivery only for customers with an account (guests can still pay online)")}

        <h3>Partial COD: advance payment</h3>
        <p className="user-email">
          To cut fake and refused COD orders, part of a COD order is paid online first (PayU) and the rest is collected in cash on
          delivery. If the order is cancelled before dispatch the advance is refunded.
        </p>
        {check("codAdvanceCoversDelivery", "Every COD order pays its delivery charge online first (the rest is cash on delivery)")}
        {check("codAdvanceEnabled", "Also require a fixed advance for high-value COD orders")}
        <div className="ac-grid">
          {num("codAdvanceAbove", "Require advance when order total is at least", { disabled: !form.codAdvanceEnabled })}
          {num("codAdvanceAmount", "Advance amount", { disabled: !form.codAdvanceEnabled })}
          {num("codAdvanceHighAbove", "Bigger advance when total is at least (optional)", { placeholder: "e.g. 5000", disabled: !form.codAdvanceEnabled })}
          {num("codAdvanceHighAmount", "Bigger advance amount", { disabled: !form.codAdvanceEnabled })}
        </div>
        <p className="user-email">When both rules apply, the larger advance is taken. It never covers the whole order.</p>

        <h3>Delivery time and where we deliver</h3>
        <p className="user-email">
          Customers see "Delivery by ..." from their PIN code. If Shiprocket is set up (SHIPROCKET_PICKUP_POSTCODE) its own estimate
          is used; these days are the fallback.
        </p>
        <div className="ac-grid">
          {num("deliveryMinDays", "Earliest delivery (days)")}
          {num("deliveryMaxDays", "Latest delivery (days)")}
        </div>
        <label>
          PIN codes we do not deliver to (comma, space or new line separated)
          <textarea
            className="ac-input"
            rows="3"
            placeholder="e.g. 190001, 744101"
            value={form.unserviceablePincodes}
            onChange={(e) => set("unserviceablePincodes", e.target.value)}
          />
        </label>

        <h3>Add-ons at checkout</h3>
        <p className="user-email">
          Cheap products offered just before payment (and a nudge towards free delivery). Only active, in-stock products priced at or
          below this limit, with no required options, are offered. 0 switches add-ons off.
        </p>
        <div className="ac-grid">{num("addOnMaxPrice", "Add-on price limit (₹)")}</div>

        <div className="ac-actions">
          <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
